package ui.resource.detail


import androidx.compose.animation.Crossfade
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import api.ResourceType
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.datetime.LocalDateTime
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.tools.EMPTY
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.navTo_VisualEditorUI
import tk.mallumo.utils.DataState
import tools.*
import ui.resource.editor.VisualEditorArgs
import ui.widget.*
import kotlin.time.Duration.Companion.milliseconds

data class ResourceArgs(
    var companyId: Long = -1L,
    var resourceId: Long = -1
)

@ComposableNavNode(ResourceArgs::class)
@Composable
fun ResourceUI() {
    with(ResourceScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }

    }
}

@Composable
fun ResourceScope.Content(nnvl: ResourceScope.Wrapper) {
    val deleteState = DialogState.remember()

    DeleteDialog(deleteState)

    val toolbarActions = remember {
        buildList {
            if (nnvl.canEdit && nnvl.imageOrVideo) add(ToolbarAction(Svg.Edit) {
                nav.navTo_VisualEditorUI(VisualEditorArgs(args.companyId, args.resourceId))
            })
            if (nnvl.canDelete) add(ToolbarAction.delete { deleteState.show() })
        }
    }

    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = nnvl.title.first,
            titleSuffix = nnvl.title.second,
            actions = toolbarActions
        )
        Row(Modifier.fillMaxSize()) {
            if (display.resolution.width > 900) {
                LeftPanel(
                    nnvl = nnvl,
                    modifier = Modifier.weight(1F)
                        .fillMaxHeight()
                )
                if (vm.tabsScreenLarge.isNotEmpty()) {
                    Spacer(
                        Modifier.width(4.dp)
                            .fillMaxHeight()
                            .background(MaterialTheme.colorScheme.tertiaryContainer)
                    )
                    RightPanel(
                        nnvl = nnvl,
                        tabs = vm.tabsScreenLarge,
                        selection = vm.selectedTabLarge.collectAsState(),
                        modifier = Modifier.weight(2F).fillMaxHeight()
                    )
                }
            } else {
                RightPanel(
                    nnvl = nnvl,
                    tabs = vm.tabsScreenSmall,
                    selection = vm.selectedTabSmall.collectAsState(),
                    modifier = Modifier.weight(1F).fillMaxHeight()
                )
            }
        }
    }
}

@Composable
fun ResourceScope.InfoPanel(nnvl: ResourceScope.Wrapper, modifier: Modifier) {
    val scrollState = rememberScrollState()
    Box(
        modifier.verticalScroll(scrollState)
            .padding(horizontal = 8.dp)
    ) {
        Column(
            Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {

            if (ResourceType.AUDIO match nnvl.data.type) {
                Space(1.dp)
            } else {
                LazyImage(
                    id = nnvl.data.id,
                    modifier = Modifier.fillMaxWidth()
                        .aspectRatio(nnvl.data.width.toFloat() / nnvl.data.height.toFloat())
                )
            }
            Text(
                text = nnvl.data.name,
                style = MaterialTheme.typography.titleLarge
            )

            HorizontalDivider(Modifier.fillMaxWidth())
            TwoFieldText(
                label = Res.string.media_detail_type.rememberString(),
                message = nnvl.type.labelRes.rememberString(),
                messageStyle = MaterialTheme.typography.bodySmall
            )

            when (nnvl.type) {
                ResourceType.DOCUMENT -> {
                    HorizontalDivider(Modifier.fillMaxWidth())
                    TwoFieldText(
                        label = Res.string.media_detail_pages.rememberString(),
                        message = nnvl.data.pages.toString(),
                        messageStyle = MaterialTheme.typography.bodySmall
                    )
                }

                ResourceType.AUDIO,
                ResourceType.VIDEO,
                ResourceType.VIDEO_ALPHA -> {
                    HorizontalDivider(Modifier.fillMaxWidth())
                    TwoFieldText(
                        label = Res.string.media_detail_duration.rememberString(),
                        message = nnvl.data.duration.milliseconds.toString(),
                        messageStyle = MaterialTheme.typography.bodySmall
                    )
                }

                ResourceType.IMAGE,
                ResourceType.ICON,
                ResourceType.TEXT,
                ResourceType.RAW,
                ResourceType.UNDEFINED,
                ResourceType.WWW_APP -> Unit
            }
            HorizontalDivider(Modifier.fillMaxWidth())
            TwoFieldText(
                label = Res.string.media_detail_size.rememberString(),
                message = nnvl.data.rememberNiceSize(),
                messageStyle = MaterialTheme.typography.bodySmall
            )

            if (nnvl.type != ResourceType.WWW_APP) {
                HorizontalDivider(Modifier.fillMaxWidth())
                TwoFieldText(
                    label = Res.string.media_detail_resolution.rememberString(),
                    message = "${nnvl.data.width} x ${nnvl.data.height}",
                    messageStyle = MaterialTheme.typography.bodySmall
                )

                HorizontalDivider(Modifier.fillMaxWidth())
                TwoFieldText(
                    label = Res.string.media_detail_aspect_ratio.rememberString(),
                    messageStyle = MaterialTheme.typography.bodySmall,
                    message = rememberAspectRatio(nnvl.data.width, nnvl.data.height).let {
                        if (nnvl.data.width > nnvl.data.height) {
                            "${maxOf(it.first, it.second)} : ${minOf(it.first, it.second)}"
                        } else {
                            "${minOf(it.first, it.second)} : ${maxOf(it.first, it.second)}"
                        }
                    })
            }

            HorizontalDivider(Modifier.fillMaxWidth())

            TwoFieldText(
                label = Res.string.media_detail_conversion_state.rememberString(),
                messageStyle = MaterialTheme.typography.bodySmall,
                message = when {
                    nnvl.data.withoutConversion -> Res.string.media_detail_conversion_state_no_conversion.rememberString()
                    nnvl.data.conversionError -> Res.string.media_detail_conversion_state_fail.rememberString()
                    nnvl.data.dtReady == LocalDateTime.EMPTY -> Res.string.media_detail_conversion_state_preparing.rememberString()
                    else -> Res.string.media_detail_conversion_state_converted.rememberString()
                }
            )

            HorizontalDivider(Modifier.fillMaxWidth())
            TwoFieldText(
                label = Res.string.media_detail_dt_create.rememberString(),
                message = nnvl.data.dtCreate.niceFormat(),
                messageStyle = MaterialTheme.typography.bodySmall
            )

            if (nnvl.data.dtReady != LocalDateTime.EMPTY && !nnvl.data.withoutConversion) {
                HorizontalDivider(Modifier.fillMaxWidth())
                TwoFieldText(
                    label = Res.string.media_detail_dt_converted.rememberString(),
                    message = nnvl.data.dtReady.niceFormat(),
                    messageStyle = MaterialTheme.typography.bodySmall
                )
            }
            Actions(nnvl, Modifier.fillMaxWidth())
            Space(1.dp)
        }
    }
}

@Composable
fun ResourceScope.Actions(nnvl: ResourceScope.Wrapper, modifier: Modifier) {
    when (nnvl.type) {
        ResourceType.WWW_APP,
        ResourceType.UNDEFINED,
        ResourceType.ICON,
        ResourceType.RAW,
        ResourceType.TEXT -> Unit

        ResourceType.DOCUMENT -> {
            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_document
            ) { action(ResourceVM.Action.DownloadPdf(nnvl.data.id)) }

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_snapshot
            ) { action(ResourceVM.Action.DownloadImage(nnvl.data.id)) }

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_thumbnail
            ) { action(ResourceVM.Action.DownloadThumbnail(nnvl.data.id)) }

        }

        ResourceType.IMAGE -> {

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_image
            ) { action(ResourceVM.Action.DownloadImage(nnvl.data.id)) }

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_thumbnail
            ) { action(ResourceVM.Action.DownloadThumbnail(nnvl.data.id)) }
        }

        ResourceType.AUDIO -> {

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_audio
            ) { action(ResourceVM.Action.DownloadAudio(nnvl.data.id)) }
        }

        ResourceType.VIDEO -> {

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_video
            ) { action(ResourceVM.Action.DownloadVideo(nnvl.data.id)) }

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_snapshot
            ) { action(ResourceVM.Action.DownloadImage(nnvl.data.id)) }

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_thumbnail
            ) { action(ResourceVM.Action.DownloadThumbnail(nnvl.data.id)) }
        }

        ResourceType.VIDEO_ALPHA -> {
            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_video
            ) { action(ResourceVM.Action.DownloadAlphaVideo(nnvl.data.id)) }

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_snapshot
            ) { action(ResourceVM.Action.DownloadImage(nnvl.data.id)) }

            DownloadAction(
                modifier = modifier, nnvl = nnvl,
                label = Res.string.media_detail_download_thumbnail
            ) { action(ResourceVM.Action.DownloadThumbnail(nnvl.data.id)) }
        }
    }
}

@Composable
private fun ResourceScope.DownloadAction(
    modifier: Modifier,
    nnvl: ResourceScope.Wrapper,
    label: StringResource,
    onClick: () -> Unit
) {
    Button(
        modifier = modifier,
        enabled = nnvl.data.dtReady != LocalDateTime.EMPTY,
        onClick = { onClick() }) {
        Icon(Svg.Download, null)
        Space(8.dp)
        Text(label.rememberString())
    }
}

@Composable
private fun Io.Resource.rememberNiceSize(): String {
    return roundBytes(size) {
        Res.string.media_detail_conversion_state_preparing.rememberString()
    }
}

@Composable
fun ResourceScope.UsagePanel(nnvl: ResourceScope.Wrapper, modifier: Modifier) {
    val scrollState = rememberScrollState()

    Box(modifier.verticalScroll(scrollState)) {
        if (nnvl.data.usage.isEmpty()) {
            Text(
                text = Res.string.media_detail_usage_none.rememberString(),
                modifier = Modifier.align(Alignment.Center)
            )
        } else {
            Column(
                verticalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.fillMaxWidth()
                    .padding(16.dp)
            ) {
                Text(
                    text = Res.string.media_detail__usage__title.rememberString(),
                    style = MaterialTheme.typography.titleMedium
                )
                nnvl.data.usage.forEach {
                    Card(
                        onClick = { action(ResourceVM.Action.NavTo(it)) },
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(
                            MaterialTheme.colorScheme.surface
                        )
                    ) {
                        Row(Modifier.padding(8.dp)) {
                            Icon(
                                imageVector = it.extractIcon(),
                                contentDescription = null,
                                modifier = Modifier.align(Alignment.CenterVertically)
                            )
                            Space(8.dp)
                            Column(Modifier.padding(8.dp)) {
                                Text(it.name)
                                Space(4.dp)
                                Text(
                                    text = it.dt.niceFormat(),
                                    style = MaterialTheme.typography.labelSmall
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Io.Resource.Usage.extractIcon(): ImageVector {
    return when {
        id.toString().endsWith("000") -> Svg.Lan
        id.toString().endsWith("001") -> Svg.Store
        id.toString().endsWith("002") -> Svg.Marker
        id.toString().endsWith("006") -> Svg.Account
        id.toString().endsWith("008") -> Svg.Device
        id.toString().endsWith("013") -> Svg.Animation_play
        id.toString().endsWith("017") -> Svg.Square_rounded_badge_outline
        else -> Svg.Layers
    }
}

@Composable
fun ResourceScope.RightPanel(
    nnvl: ResourceScope.Wrapper,
    modifier: Modifier,
    tabs: List<TabEntry<ResourceVM.TabType>>,
    selection: State<ResourceVM.TabType>
) {

    Column(modifier) {
        Crossfade(selection.value, Modifier.weight(1F).fillMaxWidth()) {
            when (it) {
                ResourceVM.TabType.NONE -> Unit
                ResourceVM.TabType.INFO -> InfoPanel(nnvl, Modifier.fillMaxSize())
                ResourceVM.TabType.USAGE -> UsagePanel(nnvl, Modifier.fillMaxSize())
            }
        }
        if (tabs.size > 1) {
            NavigationBar(Modifier.fillMaxWidth()) {
                tabs.forEach { tabDefinition ->
                    NavigationBarItem(
                        selected = tabDefinition.tag == selection.value,
                        onClick = {
                            with(tabDefinition) { onClick(tabDefinition) }
                        },
                        label = { Text(tabDefinition.label.rememberString()) },
                        icon = { Icon(tabDefinition.icon, null) })
                }
            }
        }
    }
}

@Composable
fun ResourceScope.LeftPanel(nnvl: ResourceScope.Wrapper, modifier: Modifier) {
    Box(modifier = modifier) {
        InfoPanel(nnvl, Modifier.fillMaxSize())
    }
}


@Composable
fun ResourceScope.DeleteDialog(deleteState: DialogState) {
    val confirmButton: @Composable () -> Unit = {
        if (vm.state.entry?.data?.usage?.isEmpty() == true) {
            TextButton({
                action(ResourceVM.Action.Delete)
                deleteState.hide()
            }) { Text(Res.string.dialog__action_delete.rememberString()) }
        }
    }
    Dialog(deleteState) {
        DialogUI(
            Modifier.align(Alignment.Center),
            onDismissRequest = { deleteState.hide() },
            title = { Text(Res.string.media_detail__delete_title.rememberString()) },
            confirmButton = confirmButton,
            dismissButton = { TextButton({ deleteState.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            if (vm.state.entry?.data?.usage?.isEmpty() == true) {
                Text(Res.string.media_detail__delete_message.rememberString())
            } else {
                Text(Res.string.media_detail__delete_warn.rememberString())
            }

        }
    }
}
